import type { Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

export type ReplaceSelectionSession = {
  frozenRange: { from: number; to: number };
  currentRange: { from: number; to: number };
  lastAppliedContent: string | null;
  selectionKind: 'inline' | 'block';
};

const replaceSelectionSessions = new Map<string, Map<string, ReplaceSelectionSession>>();

function getOrCreateRequestSessions(requestId: string): Map<string, ReplaceSelectionSession> {
  if (!replaceSelectionSessions.has(requestId)) {
    replaceSelectionSessions.set(requestId, new Map());
  }

  return replaceSelectionSessions.get(requestId)!;
}

export function getReplaceSelectionSession(requestId: string, toolCallId: string): ReplaceSelectionSession | null {
  return replaceSelectionSessions.get(requestId)?.get(toolCallId) ?? null;
}

export function ensureReplaceSelectionSession({
  requestId,
  toolCallId,
  editor,
  fallbackRange,
}: {
  requestId: string;
  toolCallId: string;
  editor: Editor;
  fallbackRange?: { from: number; to: number };
}): ReplaceSelectionSession | null {
  const existing = getReplaceSelectionSession(requestId, toolCallId);
  if (existing) {
    return existing;
  }

  const frozenRange = getAiToolkit(editor).getActiveSelection() ?? fallbackRange;
  if (!frozenRange) {
    return null;
  }

  const session: ReplaceSelectionSession = {
    frozenRange,
    currentRange: frozenRange,
    lastAppliedContent: null,
    selectionKind: detectSelectionKind(editor, frozenRange),
  };

  getOrCreateRequestSessions(requestId).set(toolCallId, session);
  return session;
}

function detectSelectionKind(editor: Editor, range: { from: number; to: number }): 'inline' | 'block' {
  try {
    const $from = editor.state.doc.resolve(range.from);
    const $to = editor.state.doc.resolve(range.to);

    return $from.parent.inlineContent && $to.parent.inlineContent ? 'inline' : 'block';
  } catch {
    return 'block';
  }
}

export function clearReplaceSelectionSessionsForRequest(requestId: string): void {
  replaceSelectionSessions.delete(requestId);
}

export function clearAllReplaceSelectionSessions(): void {
  replaceSelectionSessions.clear();
}
